
import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Brain, GraduationCap, LineChart } from 'lucide-react';

interface BenefitProps {
  title: string;
  description: string;
  icon: React.ElementType;
  delay: number;
}

const Benefit: React.FC<BenefitProps> = ({ title, description, icon: Icon, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="flex gap-4"
    >
      <div className="bg-gradient-to-br from-[#FF9933]/10 via-white/10 to-[#138808]/10 p-3 rounded-xl h-fit animate-float">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </motion.div>
  );
};

const benefits = [
  {
    icon: Clock,
    title: "Save Time",
    description: "Edaptica's smart algorithm focuses your study time on areas that need the most attention, making your preparation more efficient."
  },
  {
    icon: Brain,
    title: "Personalized Learning",
    description: "Our AI understands your strengths and weaknesses, creating a tailored learning experience that adapts to your progress."
  },
  {
    icon: GraduationCap,
    title: "Improved Results",
    description: "Students using Edaptica see significant improvements in their test scores through targeted practice and feedback."
  },
  {
    icon: LineChart,
    title: "Track Progress",
    description: "Detailed analytics help you visualize your improvement over time and identify areas that need more focus."
  }
];

export function Benefits() {
  return (
    <section className="py-20 bg-white overflow-hidden relative">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-3xl -z-10"></div>
      
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF9933]/20 via-[#FFFFFF]/20 to-[#138808]/20 transform rotate-3 rounded-2xl"></div>
            <img 
              src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop" 
              alt="Student using Edaptica" 
              className="rounded-2xl shadow-lg relative z-10"
            />
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-lg p-4 max-w-xs"
            >
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <GraduationCap className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Proven Success</p>
                  <p className="text-xs text-gray-600">85% of our students improve their scores by 30%+</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-block bg-primary/10 rounded-full px-4 py-1 mb-4">
                <span className="text-primary font-medium text-sm">Why Choose Edaptica</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Benefits of Adaptive Learning</h2>
              <p className="text-xl text-gray-600">
                Experience a revolutionary way to prepare for your exams with our AI-powered platform.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <Benefit 
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                  delay={index * 0.1}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
